// FeDetailModal.jsx
import React from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Table,
  Form,
  Card,
  Container,
} from "react-bootstrap";
import ReactToPrint from "react-to-print";
import api from "../../../Services/Api/api";
import { INR, fmtDate, fmtDateTime } from "../../../Modules/utils/format";
import { getFullName } from "../../../Modules/utils/fullname";
import { formatTypeLabel } from "../TransactionsScreen/FormSteps/SelectTransactionType";
import {
  formatPhone,
  getEndOfDay,
  toDateInputFormat,
  toDateInputValue,
} from "../../../Modules/utils/etc";

/**
 * FeDetailModal
 *
 * Props:
 * - show (bool)
 * - agent (agent object from summary list) { _id, fullName, phone ... }
 * - onClose()
 * - onCloseAndReload()
 *
 * Behavior:
 * - Fetch detailed data for agent when opened:
 *   GET v1/summary/:agentId?range=...
 * - Expect response:
 *   {
 *     agent: { ... },
 *     header: { office, startDate, endDate, generatedAt },
 *     totals: { cod:{total, paid, unpaid, totalBalance}, commission:{...}, payments:{...} },
 *     codByDate: [{date, codCollected, codPaid, rtoUnpaid, rtoPaid, runningCod}],
 *     commissionByDate: [...],
 *     rtoLossList: [...],
 *     adjustments: [...],
 *     ledger: [ { date, type, codDelta, rtoDelta, commDelta, paidToFe, depositByFe, adjustment, runningCodBalance, runningCommBalance, note } ]
 *   }
 *
 * - Printable view uses same data but formatted for A4.
 */

class FeDetailModal extends React.Component {
  state = {
    loading: false,
    error: null,
    data: null,
    printableMode: false,
    range: "",
    startDate: null,
    endDate: null,
  };

  componentDidUpdate(prev) {
    if (this.props.show && !prev.show) {
      const r = this.props.initialRange;

      this.setState(
        {
          ...r,
        },
        this.fetchDetails
      );
    }

    if (!this.props.show && prev.show) {
      this.setState({
        data: null,
        error: null,
        loading: false,
        printableMode: false,
        startDate: null,
        endDate: null,
      });
    }
  }

  getDateRange(rangeKey) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let start = null;
    let end = null;

    switch (rangeKey) {
      case "today":
        start = today;
        end = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          23,
          59,
          59,
          999
        );
        break;

      case "this_week": {
        const day = today.getDay(); // 0 = Sun
        const mondayOffset = day === 0 ? -6 : 1 - day;
        start = new Date(today);
        start.setDate(today.getDate() + mondayOffset);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      }

      case "last_week": {
        const day = today.getDay();
        const mondayOffset = day === 0 ? -6 : 1 - day;

        const thisMonday = new Date(today);
        thisMonday.setDate(today.getDate() + mondayOffset);

        start = new Date(thisMonday);
        start.setDate(thisMonday.getDate() - 7);

        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      }

      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        break;

      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(
          today.getFullYear(),
          today.getMonth(),
          0,
          23,
          59,
          59,
          999
        );
        break;

      case "prev_last_month": // optional if needed
        start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        end = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          0,
          23,
          59,
          59,
          999
        );
        break;

      case "since_settlement":
        // handled by backend or another field - leave empty
        return { start: null, end: null };

      default:
        // custom or unsupported -> no range
        return { start: null, end: null };
    }

    return { start, end };
  }

  buildQuery() {
    const { range, startDate, endDate } = this.state;

    // CUSTOM RANGE
    if (range === "custom") {
      if (startDate) {
        return { startDate: startDate, endDate: endDate || new Date() };
      }
    }

    // PRESET RANGE (today, this_week, last_month, etc.)
    const { start, end } = this.getDateRange(range);

    return { startDate: start, endDate: end };
  }

  fetchDetails = async () => {
    try {
      this.setState({ loading: true, error: null });
      const agentId = this.props.agent._id;

      const params = this.buildQuery();
      if (this.state.startDate && this.state.endDate) {
        params.startDate = this.state.startDate;
        params.endDate = this.state.endDate;
      }

      const res = await api.get(`v1/office/summary/agent/${agentId}`, params);

      // expected 'res' to match shape described above
      this.setState({ data: res, loading: false });
    } catch (e) {
      this.setState({
        loading: false,
        error: e.message || "Failed to load details",
      });
    }
  };

  getPrintableContent = () => {
    return this.printRef;
  };

  handleRangeChange = (e) => {
    const range = e.target.value;

    if (range === "custom") {
      this.setState({ range });
    } else {
      this.setState({ range }, this.fetchDetails);
    }
  };

  renderRangeSelector() {
    const { range, startDate, endDate } = this.state;

    return (
      <div className="mb-3">
        <Row className="g-2 align-items-end">
          {/* Range Dropdown */}
          <Col sm={4} md={3}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Range</Form.Label>
              <Form.Select
                value={range || "since_settlement"}
                onChange={this.handleRangeChange}
              >
                <option value="since_settlement">From Last Settlement</option>
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="custom">Custom Range</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Start Date (only for custom) */}
          {range === "custom" && (
            <Col sm={4} md={3}>
              <Form.Group>
                <Form.Label className="small fw-semibold">
                  Start Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={startDate || ""}
                  onChange={(e) => this.setState({ startDate: e.target.value })}
                />
              </Form.Group>
            </Col>
          )}

          {/* End Date (only for custom) */}
          {range === "custom" && (
            <Col sm={4} md={3}>
              <Form.Group>
                <Form.Label className="small fw-semibold">
                  End Date a
                </Form.Label>
                <Form.Control
                  type="date"
                  value={toDateInputValue(endDate || "")}
                  onChange={(e) =>
                    this.setState({ endDate: getEndOfDay(e.target.value) })
                  }
                />
              </Form.Group>
            </Col>
          )}

          {/* Apply Button */}
          <Col sm={4} md="auto" className="mt-2 mt-md-4">
            <Button
              size="sm"
              variant="primary"
              onClick={this.fetchDetails}
              disabled={range === "custom" && (!startDate || !endDate)}
            >
              Apply Range
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  renderHeaderSection(d) {
    const officeName = getFullName(d.office);
    const agent = d.agent || {};
    const rangeStr = `${fmtDate(d.dateRange?.start)} → ${
      fmtDate(d.dateRange?.end) || "Present"
    }`;

    return (
      <>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{officeName}</div>
          <div style={{ fontWeight: 600, fontSize: 14, marginTop: 4 }}>
            {getFullName(agent)}
            <span className="small text-muted" style={{ marginLeft: 8 }}>
              ({formatPhone(agent)})
            </span>
          </div>
          <div className="small text-muted" style={{ marginTop: 4 }}>
            Period: {rangeStr}
            <span style={{ marginLeft: 12 }}>
              Generated: {fmtDateTime(d.header?.generatedAt || new Date())}
            </span>
          </div>
        </div>

        <hr style={{ borderColor: "#eee", margin: "8px 0 12px" }} />
      </>
    );
  }

  renderTopSummary(data = {}) {
    const { totals = {}, lastTr, rtoLossList } = data;

    // small render helpers
    const format = (v) => (typeof INR === "function" ? INR(v) : String(v));
    const renderRowItem = (label, value = 0, opts = {}) => {
      const valueStyle = {
        fontFamily: "monospace",
        // fontWeight: 600,
        color: opts.color || "inherit",
      };
      return (
        <div
          className="tsb-item"
          key={label}
          title={opts.title || label}
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "0px 0",
            ...(opts.style || {}),
          }}
        >
          <div style={{ fontSize: 11, color: "#6c757d" }}>{label}</div>
          <div style={{ fontSize: 12, ...valueStyle }}>
            {(opts.format || format)(value)}
          </div>
        </div>
      );
    };

    return (
      <Container fluid className="tsb-root" style={{ whiteSpace: "nowrap" }}>
        {/* Top Row: 3 cards (each with 4 items) */}
        <Row className="mb-1" xs={1} md={2}>
          <Col className="mb-1">
            <Card>
              <Card.Body>
                <Card.Title style={{ fontSize: 14, fontWeight: 600 }}>
                  Operational Summary
                </Card.Title>
                <div style={{ marginTop: 8 }}>
                  {renderRowItem("Total COD", totals.totalCOD)}

                  {renderRowItem("Total Pickups", totals.pickupCount, {
                    format: (x) => x,
                  })}
                  {renderRowItem("Total Deliveries", totals.deliveryCount, {
                    format: (x) => x,
                  })}
                  {renderRowItem(
                    "Total Commission",
                    totals.commission?.computedTotal
                  )}
                  {renderRowItem("RTO Loss Count", rtoLossList?.length || 0, {
                    format: (x) => x,
                  })}
                  {renderRowItem("Total RTO Loss", totals.totalRtoLoss)}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col className="mb-1">
            <Card>
              <Card.Body>
                <Card.Title style={{ fontSize: 14, fontWeight: 600 }}>
                  FE Dues
                </Card.Title>
                <div style={{ marginTop: 8 }}>
                  {renderRowItem(
                    "Commission Adjusted",
                    totals.commission?.settledTotal
                  )}
                  {renderRowItem(
                    "FE to Office Amount",
                    totals.payments?.depositedByFE
                  )}
                  {renderRowItem(
                    "Office to FE Amount",
                    totals.payments?.paidToFe
                  )}

                  {renderRowItem(
                    "Opening Balance",
                    lastTr?.userBalanceDue?.total
                  )}

                  {renderRowItem(
                    "Commision Balance",
                    totals.currentCommBalance,
                    { style: { fontWeight: "bold" } }
                  )}
                  {renderRowItem(
                    "User Balance Due",
                    totals.currentUserBalance,
                    { style: { fontWeight: "bold" } }
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ledger uses numbers without ₹ symbol, per your request
  renderLedger(d) {
    const ledger = d.ledger || [];

    return (
      <div style={{ marginBottom: 18 }}>
        <div className="small text-muted mb-2">Transaction Ledger</div>

        <div style={{ overflowX: "auto" }}>
          <Table
            bordered
            size="sm"
            responsive="sm"
            className="table-fixed"
            style={{ minWidth: 1100 }}
          >
            <thead>
              <tr>
                <th style={{ width: 110 }}>Date</th>
                <th style={{ width: 160 }}>Type</th>
                <th style={{ width: 90, textAlign: "right" }}>Paid by FE</th>
                <th style={{ width: 90, textAlign: "right" }}>Paid to FE</th>

                <th style={{ width: 90, textAlign: "right" }}>Comm±</th>
                <th style={{ width: 80, textAlign: "right" }}>User Bal±</th>

                <th style={{ width: 90, textAlign: "right" }}>Total COD</th>
                <th style={{ width: 80, textAlign: "right" }}>RTO Loss</th>

                <th style={{ width: 110, textAlign: "right" }}>
                  Commission Balance
                </th>
                <th style={{ width: 110, textAlign: "right" }}>
                  Total Balance Due
                </th>

                <th style={{ width: 200 }}>Note</th>
              </tr>
            </thead>
            <tbody className="text-nowrap">
              {ledger.map((row, idx) => (
                <tr key={idx}>
                  <td style={{ verticalAlign: "middle" }}>
                    {fmtDateTime(row.date)}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {formatTypeLabel(row.type)}
                  </td>

                  <td
                    className="text-end"
                    style={{ fontFamily: "monospace", color: "green" }}
                  >
                    {(row.depositeByFe || 0).toLocaleString("en-IN")}
                  </td>
                  <td
                    className="text-end"
                    style={{ fontFamily: "monospace", color: "red" }}
                  >
                    {(row.paidToFe || 0).toLocaleString("en-IN")}
                  </td>

                  <td className="text-end" style={{ fontFamily: "monospace" }}>
                    {(row.commBalanceDelta || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace" }}>
                    {(row.userBalanceDueDelta || 0).toLocaleString("en-IN")}
                  </td>

                  <td className="text-end" style={{ fontFamily: "monospace" }}>
                    {(row.totalCOD || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace" }}>
                    {(row.rtoLoss || 0).toLocaleString("en-IN")}
                  </td>

                  <td
                    className="text-end"
                    style={{
                      fontFamily: "monospace",
                      fontWeight: 700,
                      color: "red",
                    }}
                  >
                    {(row.runningCommBalance || 0).toLocaleString("en-IN")}
                  </td>
                  <td
                    className="text-end"
                    style={{
                      fontFamily: "monospace",
                      fontWeight: 700,
                      color: "green",
                    }}
                  >
                    {(row.runningUserBalanceDue || 0).toLocaleString("en-IN")}
                  </td>

                  <td style={{ whiteSpace: "nowrap", wordBreak: "break-word" }}>
                    {row.note || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  }

  renderRtoList(d) {
    const list = d.rtoLossList || [];
    if (!list.length) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <div className="small text-muted mb-2">RTO Loss Details</div>
        <Table size="sm" bordered responsive="sm">
          <thead>
            <tr>
              <th>ID</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody className="text-nowrap">
            {list.map((r, i) => (
              <tr key={i}>
                <td>{r.id || `RTO-${i + 1}`}</td>
                <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                  {(r.amount || 0).toLocaleString("en-IN")}
                </td>
                <td>{fmtDate(r.date)}</td>
                <td>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  renderAdjustments(d) {
    const list = d.adjustments || [];
    if (!list.length) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <div className="small text-muted mb-2">Adjustments</div>
        <Table size="sm" bordered responsive="sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody className="text-nowrap">
            {list.map((a, i) => (
              <tr key={i}>
                <td>{fmtDateTime(a.date)}</td>
                <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                  {(a.amount || 0).toLocaleString("en-IN")}
                </td>
                <td>{a.reason || "-"}</td>
                {/* <td>{a.approvedBy || "-"}</td> */}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  renderPrintable(d) {
    // A4-optimized printable content
    return (
      <div
        style={{ padding: 16, width: "100%", boxSizing: "border-box" }}
        ref={(el) => (this.printRef = el)}
      >
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {d.header?.office?.firstName || "Office"}
          </div>
          <div style={{ fontWeight: 600 }}>
            {d.agent?.fullName || d.agent?.firstName}
          </div>
          <div className="small text-muted">
            {d.header?.rangeLabel ||
              `${fmtDate(d.header?.startDate)} → ${fmtDate(d.header?.endDate)}`}
          </div>
        </div>

        <hr style={{ borderColor: "#ddd" }} />

        {/* Summaries printed with ₹ symbol */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>Total COD Amount</div>
            <div style={{ fontWeight: 700 }}>
              {INR(d.totals?.cod?.total || 0)}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>Total COD Paid</div>
            <div style={{ fontWeight: 700 }}>
              {INR(d.totals?.cod?.paid || 0)}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>Unpaid RTO Loss</div>
            <div style={{ fontWeight: 700 }}>
              {INR(d.totals?.cod?.rtoUnpaid || 0)}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 6,
            }}
          >
            <div>Current COD Balance</div>
            <div style={{ fontWeight: 900 }}>
              {INR(d.totals?.cod?.currentBalance || 0)}
            </div>
          </div>
        </div>

        <hr style={{ borderColor: "#eee" }} />

        {this.renderLedger(d)}
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>Auditor Signature</div>
            <div>FE Signature</div>
          </div>
          <div style={{ marginTop: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>_______________________</div>
              <div>_______________________</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderBody() {
    const { data, loading, error } = this.state;
    if (loading) return <div className="text-muted">Loading...</div>;
    if (error) return <div className="text-danger">{error}</div>;
    if (!data) return null;

    return (
      <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 6 }}>
        {this.renderRangeSelector()}
        {this.renderHeaderSection(data)}
        {this.renderTopSummary(data)}

        {data.summaryByDate && data.summaryByDate.length ? (
          <>
            <div className="small text-muted mb-2">Summary by Date</div>
            <Table size="sm" bordered responsive="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>COD Collected</th>
                  <th>FE to Office</th>
                  <th>Office to FE</th>
                  <th>Commission</th>
                  <th>Settlement</th>
                  <th>RTO Loss</th>
                  <th>Current Balance Due</th>
                  <th>Total Balance Due</th>
                  <th>Commission Bal</th>
                </tr>
              </thead>
              <tbody className="text-nowrap">
                {data.summaryByDate.map((r, i) => (
                  <tr key={i}>
                    <td>{fmtDate(r.date)}</td>
                    {[
                      "codCollected",
                      "depositedByFE",
                      "paidToFE",
                      "commission",
                      "settlement",
                      "rtoLoss",
                      "currentBalDue",
                    ].map((x) => (
                      <td
                        key={x}
                        style={{ textAlign: "right", fontFamily: "monospace" }}
                      >
                        {(r[x] || 0).toLocaleString("en-IN")}
                      </td>
                    ))}

                    <td
                      style={{
                        textAlign: "right",
                        fontFamily: "monospace",
                        fontWeight: 700,
                      }}
                    >
                      {(r.runningBalDue || 0).toLocaleString("en-IN")}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontFamily: "monospace",
                        fontWeight: 700,
                      }}
                    >
                      {(r.runningComm || 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <hr style={{ borderColor: "#eee" }} />
          </>
        ) : null}

        {/* Commission by date */}
        {data.commissionByDate && data.commissionByDate.length ? (
          <>
            <div className="small text-muted mb-2">Commission by Date</div>
            <Table size="sm" bordered responsive="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Deliveries</th>
                  <th>Pickups</th>
                  <th>Delivery Comm</th>
                  <th>Pickup Comm</th>
                  <th>Total</th>
                  <th>Running Comm</th>
                </tr>
              </thead>
              <tbody className="text-nowrap">
                {data.commissionByDate.map((r, i) => (
                  <tr key={i}>
                    <td>{fmtDate(r.date)}</td>
                    <td className="text-end">{r.deliveries || 0}</td>
                    <td className="text-end">{r.pickups || 0}</td>
                    <td
                      className="text-end"
                      style={{ fontFamily: "monospace" }}
                    >
                      {(r.deliveryComm || 0).toLocaleString("en-IN")}
                    </td>
                    <td
                      className="text-end"
                      style={{ fontFamily: "monospace" }}
                    >
                      {(r.pickupComm || 0).toLocaleString("en-IN")}
                    </td>
                    <td
                      className="text-end"
                      style={{ fontFamily: "monospace", fontWeight: 700 }}
                    >
                      {(r.total || 0).toLocaleString("en-IN")}
                    </td>
                    <td
                      className="text-end"
                      style={{ fontFamily: "monospace", fontWeight: 700 }}
                    >
                      {(r.runningTotal || 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <hr style={{ borderColor: "#eee" }} />
          </>
        ) : null}

        {this.renderRtoList(data)}
        {this.renderAdjustments(data)}

        {this.renderLedger(data)}
      </div>
    );
  }

  renderFooter() {
    const { printableMode } = this.state;

    return (
      <div className="d-flex justify-content-between mt-3">
        <Button variant="outline-secondary" onClick={this.props.onClose}>
          Close
        </Button>

        <div>
          <Button
            className="me-2"
            variant="secondary"
            onClick={() => this.setState({ printableMode: !printableMode })}
          >
            {printableMode ? "Show Details" : "Show Less"}
          </Button>

          <ReactToPrint
            content={() => this.getPrintableContent()}
            trigger={() => <Button variant="primary">Print</Button>}
          />
        </div>
      </div>
    );
  }

  render() {
    const { show } = this.props;
    const { printableMode, data } = this.state;

    return (
      <Modal
        show={!!show}
        onHide={this.props.onClose}
        size="xl"
        dialogClassName="modal-xxl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>FE Detail & Audit</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ minHeight: 240 }}>
          {/* Printable hidden area (ReactToPrint will use this ref) */}
          <div style={{ display: printableMode ? "block" : "none" }}>
            {data ? this.renderPrintable(data) : null}
          </div>

          <div style={{ display: printableMode ? "none" : "block" }}>
            {this.renderBody()}
          </div>
        </Modal.Body>

        <Modal.Footer>{this.renderFooter()}</Modal.Footer>
      </Modal>
    );
  }
}

export default FeDetailModal;
